/**
 * useSyncStatusBar — hook that controls the sync status progress bar.
 *
 * Subscribes to `db.cloud.syncState` and returns visibility + styling
 * config for the progress bar. Implements a delayed-show pattern:
 * indeterminate sync states wait 2.5s before showing (to avoid flashing
 * on quick syncs), while error/offline states show immediately.
 *
 * Race-condition-safe: uses explicit setTimeout + useRef for the delay,
 * cancelling pending timers on every state change.
 *
 * Coordinates with OfflineIndicator: suppresses the gray offline bar
 * when navigator.onLine is false (OfflineIndicator handles that case).
 *
 * See: docs/plans/SYNC_STATUS_BAR_PLAN.md
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { useObservable } from "dexie-react-hooks"
import { db } from "@/db/schema"
import { useAuthStore } from "@/features/auth/stores/authStore"

export interface SyncBarState {
  /** Whether the bar is currently visible (after any delay) */
  isVisible: boolean
  /** 'indeterminate' for active sync, 'determinate' for error/offline */
  variant: "indeterminate" | "determinate"
  /** CSS custom property name for the bar color */
  colorVar: string
}

/** Delay before showing indeterminate sync bar (ms) — prevents flash for quick syncs */
const ENTER_DELAY_SYNC = 2500
/** Delay before showing critical states like error/offline (ms) */
const ENTER_DELAY_CRITICAL = 50

/**
 * Compute bar config from Dexie Cloud sync state.
 * Pure function — no side effects.
 */
function computeBarConfig(
  status: string,
  phase: string,
): { shouldShow: boolean; variant: "indeterminate" | "determinate"; colorVar: string; enterDelay: number } {
  const isDeterminate =
    status === "offline" || status === "error" || phase === "error" || phase === "offline"

  const shouldShow =
    status === "connecting" ||
    status === "offline" ||
    status === "error" ||
    phase === "error" ||
    phase === "offline" ||
    phase === "not-in-sync" ||
    phase === "pushing" ||
    phase === "pulling"

  const enterDelay = isDeterminate ? ENTER_DELAY_CRITICAL : ENTER_DELAY_SYNC

  let colorVar: string
  if (status === "offline" || phase === "offline") {
    colorVar = "--border"
  } else if (status === "error" || phase === "error") {
    colorVar = "--destructive"
  } else {
    colorVar = "--primary"
  }

  return { shouldShow, variant: isDeterminate ? "determinate" : "indeterminate", colorVar, enterDelay }
}

export function useSyncStatusBar(): SyncBarState {
  const rawSyncState = useObservable(db.cloud.syncState)
  const initialSyncActive = useAuthStore((s) => s.initialSyncActive)

  const status = rawSyncState?.status ?? "offline"
  const phase = rawSyncState?.phase ?? "offline"

  const [isVisible, setIsVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Compute what the bar should look like
  const config = computeBarConfig(status, phase)

  // Determine final shouldShow: suppress during initial sync,
  // and suppress offline bar when navigator.onLine is false (let OfflineIndicator handle it)
  const getSuppressed = useCallback(() => {
    if (initialSyncActive) return true
    if (
      !config.shouldShow
    ) return true
    // Suppress gray offline bar when OfflineIndicator is active
    if (
      (status === "offline" || phase === "offline") &&
      typeof navigator !== "undefined" &&
      !navigator.onLine
    ) return true
    return false
  }, [initialSyncActive, config.shouldShow, status, phase])

  useEffect(() => {
    const suppressed = getSuppressed()

    // Clear any pending timer on every state change
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (suppressed) {
      // Don't show (CSS handles the 2s fade-out via opacity transition)
      setIsVisible(false)
      return
    }

    // Should show — apply enter delay
    if (config.enterDelay <= 50) {
      // Critical states: show nearly immediately
      setIsVisible(true)
    } else {
      // Sync states: delay to avoid flashing for quick syncs
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setIsVisible(true)
      }, config.enterDelay)
    }

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [status, phase, initialSyncActive, config.enterDelay, getSuppressed])

  return {
    isVisible,
    variant: config.variant,
    colorVar: config.colorVar,
  }
}
